import { Component, inject, signal, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { NbAdjustment, NbPopoverDirective, NbPosition, NbToastrService } from '@nebular/theme';
import {
  CustomFieldCollectionDetailDto,
  CustomFieldCollectionItemDto,
  CustomFieldCollectionService,
  CustomFieldCompositionDto,
  EquipmentDetailDto,
  EquipmentMergeDto,
  EquipmentService,
} from '@artemis-software/wr-api';
import { PendingChanges } from '@guards/pending-changes.guard';
import { catalogTenantId } from '@/utils/admin-utils';
import { displayUnitSymbol } from '@shared/pipes/unit-symbols';

export interface TgaCollectionRow {
  collection: CustomFieldCollectionItemDto;
  collectionDetail?: CustomFieldCollectionDetailDto;
}

@Component({
  standalone: false,
  selector: 'wr-tga-type-collections',
  templateUrl: './tga-type-collections.component.html',
  styleUrls: ['./tga-type-collections.component.scss'],
})
export class TgaTypeCollectionsComponent implements PendingChanges {

  @ViewChild('collectionPicker') collectionPicker?: NbPopoverDirective;

  readonly NbPosition = NbPosition;
  readonly NbAdjustment = NbAdjustment;

  readonly saving$ = new BehaviorSubject(false);
  readonly isEditing = signal(false);
  readonly canEdit = signal(false);
  readonly rows = signal<TgaCollectionRow[]>([]);

  readonly searchText$ = new BehaviorSubject('');
  readonly currentFolder$ = new BehaviorSubject('');

  readonly foldersWithCount$: Observable<{ name: string; fullPath: string; count: number }[]>;
  readonly filteredCollections$: Observable<CustomFieldCollectionDetailDto[]>;

  private readonly currentTenantId = catalogTenantId();

  private readonly route = inject(ActivatedRoute);
  private readonly equipmentService = inject(EquipmentService);
  private readonly collectionService = inject(CustomFieldCollectionService);
  private readonly toastrService = inject(NbToastrService);

  tgaTypeId: string | null = null;

  constructor() {
    this.route.paramMap.subscribe(params => {
      const id = this.tgaTypeId = params.get('id');
      if (id && id !== 'new') {
        this.loadData(id);
      }
    });

    const searchTrigger$ = combineLatest([
      this.searchText$.pipe(debounceTime(300), distinctUntilChanged()),
      this.currentFolder$,
    ]).pipe(shareReplay(1));

    this.filteredCollections$ = searchTrigger$.pipe(
      switchMap(([text, folder]) =>
        this.collectionService.findAllDetailDtos({
          text: text || undefined,
          tenantId: this.currentTenantId(),
          category: !text ? folder : undefined,
          sort: { sort: 'title', order: 'ASC' },
        }),
      ),
      map(collections => {
        const selectedIds = this.selectedIds;
        return collections.filter(c => !selectedIds.has(c.id));
      }),
      shareReplay(1),
    );

    const allItems$ = this.collectionService.findAll({
      tenantId: this.currentTenantId(),
      sort: { sort: 'title', order: 'ASC' },
    }).pipe(shareReplay(1));

    this.foldersWithCount$ = combineLatest([
      searchTrigger$.pipe(
        switchMap(([text, folder]) => {
          if (text) return of([]);
          return this.collectionService.getFolders(folder);
        }),
      ),
      this.currentFolder$,
      allItems$,
    ]).pipe(
      map(([folderNames, currentFolder, allItems]) =>
        folderNames.map(name => {
          const fullPath = currentFolder ? `${currentFolder}${name}/` : `${name}/`;
          const count = allItems.filter(item => item.category.startsWith(fullPath)).length;
          return { name, fullPath, count };
        }),
      ),
      shareReplay(1),
    );
  }

  loadData(id: string): void {
    this.equipmentService.findById(id).subscribe((dto: EquipmentDetailDto) => {
      this.canEdit.set(dto.permissions.canEdit);
      const rows = this.buildRows(dto);
      this.rows.set(rows);
      this.loadCollectionDetails(rows);
    });
  }

  buildRows(dto: EquipmentDetailDto): TgaCollectionRow[] {
    return (dto.customFieldCollections ?? [])
      .slice()
      .sort((a, b) => a.ranking - b.ranking)
      .map(ec => ({
        collection: {
          id: ec.collection.id,
          title: ec.collection.title,
          description: ec.collection.description,
          tenantId: ec.collection.tenant.id,
          color: ec.collection.color,
          category: ec.collection.category,
          permissions: ec.collection.permissions,
          deleted: false,
        } as CustomFieldCollectionItemDto,
      }));
  }

  loadCollectionDetails(rows: TgaCollectionRow[]): void {
    rows.forEach((row, index) => {
      this.collectionService.findById(row.collection.id).subscribe(detail => {
        this.rows.update(current =>
          current.map((r, i) => i === index ? { ...r, collectionDetail: detail } : r),
        );
      });
    });
  }

  get selectedIds(): Set<string> {
    return new Set(this.rows().map(r => r.collection.id));
  }

  selectCollection(detail: CustomFieldCollectionDetailDto): void {
    this.rows.update(rows => [
      ...rows,
      {
        collection: {
          id: detail.id,
          title: detail.title,
          description: detail.description,
          tenantId: detail.tenant.id,
          color: detail.color,
          category: detail.category,
          permissions: detail.permissions,
          deleted: false,
        } as CustomFieldCollectionItemDto,
        collectionDetail: detail,
      },
    ]);
    this.collectionPicker?.hide();
    this.searchText$.next('');
    this.currentFolder$.next('');
  }

  removeRow(index: number): void {
    this.rows.update(rows => rows.filter((_, i) => i !== index));
  }

  onSort(): void {
    this.rows.update(r => [...r]);
  }

  get sortableOptions(): object {
    return { disabled: !this.isEditing() };
  }

  onEdit(): void {
    this.isEditing.set(true);
  }

  async cancel(): Promise<void> {
    this.isEditing.set(false);
    if (this.tgaTypeId) {
      this.loadData(this.tgaTypeId);
    }
  }

  async submit(): Promise<void> {
    if (!this.tgaTypeId) return;
    try {
      this.saving$.next(true);
      const mergeDto: EquipmentMergeDto = {
        id: this.tgaTypeId,
        collectionIds: this.rows().map(r => r.collection.id),
      };
      await firstValueFrom(this.equipmentService.merge(mergeDto));
      this.isEditing.set(false);
      this.toastrService.success('Feldgruppen gespeichert', 'Erfolg');
      this.loadData(this.tgaTypeId);
    } catch {
      this.toastrService.danger('Feldgruppen konnten nicht gespeichert werden.', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  hasPendingChanges(): boolean {
    return this.isEditing();
  }

  // ── Picker helpers ──────────────────────────────────────────────────────

  navigateToFolder(event: Event, fullPath: string): void {
    event.stopPropagation();
    this.currentFolder$.next(fullPath);
    this.searchText$.next('');
  }

  navigateToParent(event: Event): void {
    event.stopPropagation();
    const folder = this.currentFolder$.value;
    if (!folder) return;
    const parts = folder.replace(/\/$/, '').split('/');
    parts.pop();
    this.currentFolder$.next(parts.length ? parts.join('/') + '/' : '');
  }

  folderDisplayName(path: string): string {
    const trimmed = path.replace(/\/$/, '');
    return trimmed.split('/').pop() ?? path;
  }

  categoryLabel(category: string): string {
    if (!category) return 'Allgemein';
    return category.replace(/\//g, ' › ').replace(/ › $/, '');
  }

  onSearchInput(event: Event): void {
    this.searchText$.next((event.target as HTMLInputElement).value);
  }

  displayFieldType(valueType: CustomFieldCompositionDto.ValueTypeEnum): string {
    switch (valueType) {
      case 'Boolean': return 'Ja/Nein';
      case 'Integer': return 'Zahl';
      case 'Double': return 'Dezimal';
      case 'String': return 'Text';
      case 'Longtext': return 'Langtext';
      case 'LongtextWithPresets': return 'Langtext+';
      case 'MultipleChoice': return 'Auswahl';
      case 'LocalDate': return 'Datum';
      case 'LocalDateTime': return 'Datum & Zeit';
      case 'Attachment': return 'Datei';
      default: return valueType;
    }
  }

  readonly displayUnit = displayUnitSymbol;
}
